// src/pages/parent/feesManagement/payFeesColumns.js
import React, { useEffect, useState } from "react";
import moment from "moment";
import dayjs from "dayjs";
import { FaEye, FaDownload } from "react-icons/fa";
import styles from "../../schoolCockpit/master/academicConfiguration/Academiclist.module.css";
import payStyles from "./PayFees.module.css";
import CustomDatePicker from "../../../components/CustomDatePicker/CustomDatePicker";

export const formatCurrency = (value) => {
  const num = Number(value) || 0;
  return `${num < 0 ? "-" : ""}₹${Math.abs(num).toLocaleString("en-IN")}`;
};

const renderCellWithDash = ({ cell }) => {
  const value = cell.getValue();
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

const renderAmountCell = ({ cell }) => formatCurrency(cell.getValue());

/**
 * "Paid" -> green, "Not Paid" -> red, "Partially Paid" -> amber.
 */
export const getFeeStatusBadge = (statusText) => {
  if (!statusText) return "-";
  const key = String(statusText).toLowerCase().replace(/[-\s]/g, "");

  const classMap = {
    paid: styles.statusApproved,
    notpaid: styles.statusRejected,
    partiallypaid: styles.statusResubmitted,
  };

  return (
    <span className={`${styles.statusBadge} ${classMap[key] || ""}`}>
      {statusText}
    </span>
  );
};

/**
 * Options shown in the Fee Status column HEADER filter dropdown.
 */
const FEE_STATUS_FILTER_OPTIONS = [
  { text: "Paid", value: "Paid" },
  { text: "Not Paid", value: "Not Paid" },
];

/**
 * Due Date range filter using the reusable CustomDatePicker.
 * Commits the value as [start, end] on the column so the data hook
 * can turn it into start_date / end_date payload fields.
 */
const DateRangeFilter = ({ column, translations }) => {
  const filterValue = column.getFilterValue();
  const extStart =
    Array.isArray(filterValue) && filterValue[0] ? filterValue[0] : "";
  const extEnd =
    Array.isArray(filterValue) && filterValue[1] ? filterValue[1] : "";

  const [localStart, setLocalStart] = useState(extStart);
  const [localEnd, setLocalEnd] = useState(extEnd);

  useEffect(() => {
    setLocalStart(extStart);
    setLocalEnd(extEnd);
  }, [extStart, extEnd]);

  const startValue = localStart ? dayjs(localStart) : null;
  const endValue = localEnd ? dayjs(localEnd) : null;

  const commitFilter = (nextStart, nextEnd) => {
    if (!nextStart && !nextEnd) {
      column.setFilterValue(undefined);
    } else {
      column.setFilterValue([nextStart || "", nextEnd || ""]);
    }
  };

  const handleStartChange = (dayjsDate) => {
    const formatted = dayjsDate ? dayjs(dayjsDate).format("YYYY-MM-DD") : "";
    setLocalStart(formatted);
    commitFilter(formatted, localEnd);
  };

  const handleEndChange = (dayjsDate) => {
    const formatted = dayjsDate ? dayjs(dayjsDate).format("YYYY-MM-DD") : "";
    setLocalEnd(formatted);
    commitFilter(localStart, formatted);
  };

  return (
    <div className={payStyles.dateRangeFilter}>
      <CustomDatePicker
        name={`${column.id}_start`}
        value={startValue}
        onChange={handleStartChange}
        placeholder={translations?.labels?.startDatePlaceholder || "Start Date"}
        format="DD/MM/YYYY"
        size="sm"
        isClearable={true}
        maxDate={endValue || undefined}
        className={payStyles.dateRangePicker}
      />
      <CustomDatePicker
        name={`${column.id}_end`}
        value={endValue}
        onChange={handleEndChange}
        placeholder={translations?.labels?.endDatePlaceholder || "End Date"}
        format="DD/MM/YYYY"
        size="sm"
        isClearable={true}
        minDate={startValue || undefined}
        className={payStyles.dateRangePicker}
      />
    </div>
  );
};

/**
 * Build the Pay Fees table columns.
 *
 * @param opts.translations   i18n labels
 * @param opts.onView         callback(rowData) — eye icon click
 * @param opts.onDownload     callback(rowData) — download icon click
 */
export const buildPayFeesColumns = (opts) => {
  const {
    translations = {},
    onView = null,
    onDownload = null,
    isDownloading = false,
  } = opts;

  return [
    {
      accessorKey: "chalan_no",
      header: translations?.labels?.chalanNo || "Chalan No",
      enableSorting: true,
      enableColumnFilter: false,
      Cell: renderCellWithDash,
    },
    {
      accessorKey: "group_name",
      header: translations?.labels?.feesName || "Fees Name",
      enableSorting: true,
      enableColumnFilter: false,
      Cell: renderCellWithDash,
    },
    {
      accessorKey: "paid_amount",
      header: translations?.labels?.paidAmount || "Paid Amount (₹)",
      enableSorting: true,
      enableColumnFilter: false,
      Cell: renderAmountCell,
    },
    {
      accessorKey: "balance_amount",
      header: translations?.labels?.balanceAmount || "Balance Amount (₹)",
      enableSorting: true,
      enableColumnFilter: false,
      Cell: renderAmountCell,
    },
    {
      accessorKey: "net_amount",
      header: translations?.labels?.totalAmount || "Total Amount (₹)",
      enableSorting: true,
      enableColumnFilter: false,
      Cell: renderAmountCell,
    },
    {
      accessorKey: "due_date",
      header: translations?.labels?.dueDate || "Due Date",
      enableSorting: true,
      enableColumnFilter: true,
      Filter: ({ column }) => (
        <DateRangeFilter column={column} translations={translations} />
      ),
      Cell: ({ cell }) => {
        const v = cell.getValue();
        if (!v) return "-";
        return moment(v).format("DD/MM/YYYY");
      },
      size: 280,
    },
    {
      accessorKey: "fee_status",
      header: translations?.labels?.feeStatus || "Fees Status",
      enableSorting: true,
      enableColumnFilter: true,
      filterVariant: "select",
      filterSelectOptions: FEE_STATUS_FILTER_OPTIONS,
      muiFilterTextFieldProps: {
        placeholder: translations?.labels?.feeStatus || "Fees Status",
      },
      Cell: ({ cell }) => getFeeStatusBadge(cell.getValue()),
      size: 200,
    },
    {
      id: "actions",
      header: translations?.labels?.action || "Option",
      enableSorting: false,
      enableColumnFilter: false,
      Cell: ({ row }) => {
        const disabled = Number(row.original.assign_status) === 0;
        const downloadDisabled = disabled || isDownloading;
        return (
          <div className={styles.actionGroup}>
            <button
              className={`${styles.viewBtn} ${disabled ? styles.viewBtnDisabled : ""}`}
              disabled={disabled}
              title={translations?.labels?.view || "View"}
              onClick={() => !disabled && onView?.(row.original)}
            >
              <FaEye />
            </button>
            <button
              className={`${styles.viewBtn} ${downloadDisabled ? styles.viewBtnDisabled : ""}`}
              disabled={downloadDisabled}
              title={translations?.labels?.downloadReceipt || "Download"}
              onClick={() => !downloadDisabled && onDownload?.(row.original)}
            >
              <FaDownload />
            </button>
          </div>
        );
      },
    },
  ];
};
